import { reportWeekday } from './report-zone'
import type {
  ReportDimension,
  ReportRow,
} from '@/features/reports/api/use-sales-report-query'

type Translate = (key: string, options?: Record<string, unknown>) => string

const dayFormatter = new Intl.DateTimeFormat('de-AT', {
  day: '2-digit',
  month: '2-digit',
})
const weekdayFormatter = new Intl.DateTimeFormat('de-AT', { weekday: 'short' })

/** Mo, Di, … in the order the heatmap draws them; index 0 is ISO day 1. */
export const WEEKDAY_SHORT: Array<string> = [
  'Mo',
  'Di',
  'Mi',
  'Do',
  'Fr',
  'Sa',
  'So',
]

/**
 * How a row's key reads to a person (CHG-2026-09-manager-reporting).
 *
 * The server sends keys, not sentences — a date, a channel constant, a daypart name — so the
 * wording lives here and is translated. A key the client does not know renders as itself rather
 * than as an empty cell: an unlabelled row is recoverable, a blank one is not.
 */
export function formatRowLabel(
  t: Translate,
  dimension: ReportDimension,
  row: Pick<ReportRow, 'key' | 'label'>,
  options?: { withDate?: boolean },
): string {
  switch (dimension) {
    case 'time_day':
      return formatDayKey(row.key)
    case 'time_hour':
      return formatHourKey(row.key, options?.withDate)
    case 'time_week':
      return formatWeekKey(t, row.key)
    case 'weekday_hour':
      return formatWeekdayHourKey(row.key)
    case 'daypart':
      return t(`dayparts.${row.key}`, { defaultValue: row.key })
    case 'channel':
      return t(`channels.${row.key}`, { defaultValue: row.key })
    case 'payment_method':
      return t(`paymentMethods.${row.key}`, { defaultValue: row.key })
    case 'category_main':
    case 'category_sub':
      return row.key === '__unknown__'
        ? t('groups.unknown')
        : (row.label ?? t('groups.unknown'))
    default:
      return row.label ?? row.key
  }
}

/** `Do · 03.09.` — the weekday matters more than the year on a fortnight of bars. */
export function formatDayKey(key: string): string {
  const at = new Date(`${key}T12:00:00Z`)
  if (Number.isNaN(at.getTime())) return key
  return `${weekdayFormatter.format(at)} · ${dayFormatter.format(at)}`
}

/**
 * `12:00`, or `Mo · 24.08. 12:00` over a window longer than a day.
 *
 * The hour is the unit, so the minutes are always zero and stay for readability. The date is not
 * always there because a single day's hours do not need repeating it — but over a week the bare
 * hour repeats seven times with nothing to tell the rows apart, which is a table that cannot be
 * read at all.
 */
export function formatHourKey(key: string, withDate = false): string {
  const hour = key.slice(11, 13)
  if (hour.length !== 2) return key
  return withDate
    ? `${formatDayKey(key.slice(0, 10))} ${hour}:00`
    : `${hour}:00`
}

/**
 * True when the hour rows on screen come from more than one calendar day.
 *
 * Read off the rows rather than the filter: what the label has to disambiguate is what is in the
 * table, and a window can be two days long while the sales in it all fall on one.
 */
export function hourRowsSpanDays(rows: Array<Pick<ReportRow, 'key'>>): boolean {
  if (rows.length < 2) return false
  const first = rows[0].key.slice(0, 10)
  return rows.some((row) => row.key.slice(0, 10) !== first)
}

/** `KW 36, 2026`. */
export function formatWeekKey(t: Translate, key: string): string {
  const [year, week] = key.split('-W')
  if (!year || !week) return key
  return t('period.weekLabel', { week: Number(week), year })
}

/** `Do 12:00` — one heatmap cell. */
export function formatWeekdayHourKey(key: string): string {
  const [isoDay, hour] = key.split('-')
  const name = WEEKDAY_SHORT[Number(isoDay) - 1]
  if (!name || !hour) return key
  return `${name} ${hour}:00`
}

/** True when the row is the last, still-running day of the window. */
export function isRunningDay(
  dimension: ReportDimension,
  key: string,
  today: string,
): boolean {
  return dimension === 'time_day' && key === today
}

/** Saturday and Sunday, which this store barely trades on — the trend bands them. */
export function isWeekend(dayKey: string): boolean {
  const iso = reportWeekday(dayKey)
  return iso === 6 || iso === 7
}
